/**
 * S7 review E D-2: when "Forget…" may drop a finished job whose staged upload files are still in a share. Forget is for
 * a connection that can NEVER clean them — deleted, read-only, or re-scoped so the job's share lies outside it — never
 * for one that is only disconnected: that one connects and Clear finished removes the files. Main refuses a Forget for
 * such a connection (`ConnectFirst`, `guardedRecovery.ts`) with the reason below, and the drawer shows "Connect to
 * clean up" instead of "Forget…".
 *
 * `transferJobWithinScope` is the drawer's reading of main's `isWithinScope` (`electron/azure-storage/scopeGuard.ts`)
 * for the file refs a file upload job carries — the only jobs that keep remote leftovers. `transferForgetScope.test.ts`
 * pins it against main's rule over the §3.7 scope roots. Any other ref counts as inside the scope: the drawer then
 * offers "Connect to clean up", never a Forget that main would refuse. Pure.
 */
import type { StorageAttachmentScope, StorageResourceRef } from '../../types/azureStorage';
import type { TransferJob } from '../../types/azureStorageTransfers';

export const CONNECT_TO_CLEAN_UP_REASON = 'This storage account can still delete the staged upload copies and release the locks once connected: connect it, then use Discard unfinished….';

type FileFamilyRef = Extract<StorageResourceRef, { kind: 'share' | 'directory' | 'file' }>;

const isFileFamily = (ref: StorageResourceRef): ref is FileFamilyRef => ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file';
const sameSnapshot = (rootSnapshot: string | undefined, ref: FileFamilyRef): boolean => !rootSnapshot || ref.snapshot === rootSnapshot;
const underDirectory = (path: string, root: string): boolean => path === root || root === '' || path.startsWith(`${root.replace(/\/+$/, '')}/`);

function rootAdmits(root: StorageResourceRef, ref: FileFamilyRef): boolean {
  switch (root.kind) {
    case 'account':
      return true;
    case 'service':
      return root.service === 'file';
    case 'share':
      return ref.share === root.share && sameSnapshot(root.snapshot, ref);
    case 'directory':
      return ref.kind !== 'share' && ref.share === root.share && sameSnapshot(root.snapshot, ref) && underDirectory(ref.path, root.path);
    case 'file':
      return ref.kind === 'file' && ref.share === root.share && ref.path === root.path && sameSnapshot(root.snapshot, ref);
    default:
      return false;
  }
}

/** Every remote file ref of `job` (sources, source, destination) lies inside `scope`. */
export function transferJobWithinScope(scope: StorageAttachmentScope, job: Pick<TransferJob, 'source' | 'sources' | 'destination'>): boolean {
  if (scope.kind === 'account') return true;
  const refs = [...(job.sources ?? []), job.source, job.destination].flatMap((endpoint) => (endpoint.kind === 'remote' ? [endpoint.resource] : []));
  return refs.every((ref) => !isFileFamily(ref) || rootAdmits(scope.resource, ref));
}
